import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { Loader2, TrendingUp, Filter, DollarSign, Target, Flame, History, Sparkles, Download, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtVal = (v, c) => `${c} ${v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${Math.round(v / 1e3)}K` : Math.round(v)}`;
const money = (obj) => Object.entries(obj || {}).map(([c, v]) => fmtVal(v, c)).join(' · ') || '—';

const Kpi = ({ label, value, sub, testId }) => (
  <Card data-testid={testId}>
    <CardContent className="pt-4 pb-3">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
      <p className="text-xl font-bold text-[#0F2040]">{value}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </CardContent>
  </Card>
);

const Bar = ({ pct, cls = 'bg-[#4ABFCF]' }) => (
  <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
    <div className={`h-full rounded-full ${cls}`} style={{ width: `${Math.max(2, Math.min(100, pct || 0))}%` }} />
  </div>
);

const STAGE_LABELS = { drafting: 'Drafting', review: 'Review', submitted: 'Submitted' };

export const RfpInsightsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [narrative, setNarrative] = useState(null);
  const [narrBusy, setNarrBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const navigate = useNavigate();

  const genNarrative = async () => {
    setNarrBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/rfp/response/insights/narrative`, {}, { withCredentials: true });
      setNarrative(res.data?.narrative || '');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not generate the summary'); }
    finally { setNarrBusy(false); }
  };

  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await axios.get(`${API_URL}/api/rfp/response/insights/dashboard/export/pdf`,
        { withCredentials: true, responseType: 'blob' });
      saveAs(res.data, `RFP-Insights_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch { toast.error('Could not export the PDF'); }
    finally { setPdfBusy(false); }
  };

  useEffect(() => {
    let dead = false;
    axios.get(`${API_URL}/api/rfp/response/insights/dashboard`, { withCredentials: true })
      .then(res => { if (!dead) setData(res.data); })
      .catch(() => { if (!dead) setData(null); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" /></div>;
  if (!data) return <p className="text-sm text-slate-400 py-10 text-center" data-testid="rfp-dash-error">Could not load RFP insights.</p>;

  const maxStage = Math.max(1, ...data.funnel.stages.map(s => s.count), data.funnel.closed);
  const maxQ = Math.max(1, ...data.quarters.map(q => q.won + q.lost));
  const maxGap = Math.max(1, ...data.gaps.map(g => g.gaps));
  const r = data.reuse;

  return (
    <div className="space-y-4" data-testid="rfp-insights-dashboard">
      <div className="flex items-center gap-2 justify-end">
        <Button size="sm" variant="outline" className="h-8 text-xs text-[#0F2040]" onClick={genNarrative} disabled={narrBusy}
          title="AI write-up of this dashboard for pasting into leadership updates" data-testid="rfp-dash-narrative-btn">
          {narrBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1 text-[#4ABFCF]" />}AI summary
        </Button>
        <Button size="sm" className="h-8 text-xs bg-[#0F2040] hover:bg-[#1A3055] text-white" onClick={exportPdf} disabled={pdfBusy}
          data-testid="rfp-dash-pdf-btn">
          {pdfBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}Export PDF
        </Button>
      </div>
      {narrative && (
        <Card data-testid="rfp-dash-narrative">
          <CardContent className="pt-4 pb-3 space-y-2">
            <div className="flex items-start gap-2">
              <p className="text-xs text-slate-700 whitespace-pre-wrap flex-1">{narrative}</p>
              <button onClick={async () => { try { await navigator.clipboard.writeText(narrative); toast.success('Summary copied'); } catch { toast.error('Could not copy'); } }}
                className="text-slate-400 hover:text-[#0F2040] shrink-0" title="Copy for your leadership update"
                data-testid="rfp-dash-narrative-copy"><Copy className="w-3.5 h-3.5" /></button>
            </div>
            <p className="text-[9px] text-slate-400">AI summary grounded in the dashboard numbers above — review before sharing.</p>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Win rate" value={data.win_rate != null ? `${data.win_rate}%` : '—'}
          sub={`${data.won} won / ${data.lost} lost`} testId="rfp-dash-kpi-winrate" />
        <Kpi label="Open RFPs" value={data.open} sub={`${data.funnel.closed} closed`} testId="rfp-dash-kpi-open" />
        <Kpi label="Pipeline value" value={money(data.revenue.open)} sub="open deals" testId="rfp-dash-kpi-pipeline" />
        <Kpi label="Expected revenue" value={money(data.revenue.expected)} sub="confidence-weighted" testId="rfp-dash-kpi-expected" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card data-testid="rfp-dash-funnel">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-[#0F2040]">
            <Filter className="w-4 h-4 text-[#4ABFCF]" />Pipeline funnel</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {data.funnel.stages.map(s => (
              <div key={s.key} className="flex items-center gap-2 text-xs" data-testid={`rfp-dash-funnel-${s.key}`}>
                <span className="w-20 text-slate-600">{STAGE_LABELS[s.key]}</span>
                <Bar pct={(s.count / maxStage) * 100} />
                <span className="w-8 text-right font-semibold text-[#0F2040]">{s.count}</span>
                <span className="w-24 text-[10px] text-slate-400">{s.avg_days != null ? `avg ${s.avg_days}d in stage` : ''}</span>
                {s.stalled > 0 && (
                  <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">{s.stalled} stalled ≥14d</Badge>
                )}
              </div>
            ))}
            <div className="flex items-center gap-2 text-xs">
              <span className="w-20 text-slate-600">Closed</span>
              <Bar pct={(data.funnel.closed / maxStage) * 100} cls="bg-emerald-500" />
              <span className="w-8 text-right font-semibold text-[#0F2040]">{data.funnel.closed}</span>
              <span className="text-[10px] text-slate-400">{data.win_rate != null ? `${data.win_rate}% close-won` : ''}</span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="rfp-dash-revenue">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-[#0F2040]">
            <DollarSign className="w-4 h-4 text-[#4ABFCF]" />Revenue</CardTitle></CardHeader>
          <CardContent className="space-y-1.5 text-xs">
            {[['Won value', data.revenue.won, 'text-emerald-700'], ['Lost value', data.revenue.lost, 'text-rose-700'],
              ['Open pipeline', data.revenue.open, 'text-[#0F2040]'], ['Expected (weighted)', data.revenue.expected, 'text-[#0F8A9E]']]
              .map(([label, v, cls]) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-slate-500">{label}</span>
                  <span className={`font-semibold ${cls}`}>{money(v)}</span>
                </div>
              ))}
            <div className="flex items-center justify-between pt-1 border-t border-slate-100">
              <span className="text-slate-500">Avg deal size won / lost</span>
              <span className="font-semibold text-[#0F2040]" data-testid="rfp-dash-avg-deal">
                {data.avg_deal_won != null ? fmtVal(data.avg_deal_won, '') : '—'} / {data.avg_deal_lost != null ? fmtVal(data.avg_deal_lost, '') : '—'}
              </span>
            </div>
            {data.by_size.length > 0 && (
              <div className="pt-1 space-y-1">
                {data.by_size.map(b => (
                  <div key={b.band} className="flex items-center gap-2" data-testid={`rfp-dash-size-${b.band}`}>
                    <span className="w-20 text-[10px] text-slate-500">{b.band}</span>
                    <Bar pct={b.win_rate} cls="bg-emerald-400" />
                    <span className="w-24 text-[10px] text-slate-400 text-right">{b.win_rate}% win · {b.won + b.lost} deals</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="rfp-dash-quarters">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-[#0F2040]">
            <TrendingUp className="w-4 h-4 text-[#4ABFCF]" />Win / loss by quarter</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {data.quarters.length === 0 ? <p className="text-xs text-slate-400">No closed deals yet.</p>
              : data.quarters.map(q => (
                <div key={q.quarter} className="flex items-center gap-2 text-xs" data-testid={`rfp-dash-q-${q.quarter.replace(' ', '-')}`}>
                  <span className="w-16 font-semibold text-[#0F2040]">{q.quarter}</span>
                  <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden flex">
                    <div className="h-full bg-emerald-500" style={{ width: `${(q.won / maxQ) * 100}%` }} />
                    <div className="h-full bg-rose-400" style={{ width: `${(q.lost / maxQ) * 100}%` }} />
                  </div>
                  <span className="w-28 text-[10px] text-slate-500 text-right">{q.won}W / {q.lost}L{q.win_rate != null ? ` · ${q.win_rate}%` : ''}</span>
                </div>
              ))}
            {data.by_client.length > 0 && (
              <div className="pt-2 border-t border-slate-100 space-y-1">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">By client</p>
                {data.by_client.map(c => (
                  <div key={c.client} className="flex items-center gap-2 text-[10px] text-slate-500">
                    <span className="w-32 truncate">{c.client}</span>
                    <Bar pct={c.win_rate} cls="bg-emerald-400" />
                    <span className="w-24 text-right">{c.win_rate != null ? `${c.win_rate}%` : '—'} · {c.won + c.lost} closed</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="rfp-dash-calibration">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-[#0F2040]">
            <Target className="w-4 h-4 text-[#4ABFCF]" />Confidence calibration</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            <p className="text-[10px] text-slate-400">Actual win rate at each predicted confidence band — well-calibrated scores rise left to right.</p>
            {data.calibration.map(c => (
              <div key={c.range} className="flex items-center gap-2 text-xs" data-testid={`rfp-dash-calib-${c.range}`}>
                <span className="w-16 text-slate-500">{c.range}</span>
                <Bar pct={c.win_rate ?? 0} cls={c.total ? 'bg-[#4ABFCF]' : 'bg-slate-200'} />
                <span className="w-24 text-[10px] text-slate-400 text-right">
                  {c.total ? `${c.win_rate}% win · ${c.total} deal${c.total === 1 ? '' : 's'}` : 'no deals'}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="rfp-dash-gaps">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-[#0F2040]">
            <Flame className="w-4 h-4 text-rose-500" />Capability gap heatmap</CardTitle></CardHeader>
          <CardContent className="space-y-1">
            <p className="text-[10px] text-slate-400">Topics that keep producing gap / manual answers across all RFPs — fix the library where it hurts most.</p>
            {data.gaps.length === 0 ? <p className="text-xs text-slate-400">No gap answers recorded.</p>
              : data.gaps.map(g => (
                <button key={g.topic} onClick={() => navigate(`/rfp-library?category=${encodeURIComponent(g.topic)}`)}
                  className="w-full flex items-center gap-2 text-xs hover:bg-slate-50 rounded px-0.5 transition-colors text-left"
                  title={`Open the library filtered to ${g.topic}`} data-testid={`rfp-dash-gap-${g.topic}`}>
                  <span className="w-40 truncate text-slate-600 hover:text-[#0F8A9E]">{g.topic}</span>
                  <Bar pct={(g.gaps / maxGap) * 100} cls={g.rate >= 50 ? 'bg-rose-500' : g.rate >= 25 ? 'bg-orange-400' : 'bg-amber-300'} />
                  <span className="w-28 text-[10px] text-slate-400 text-right">{g.gaps} gaps · {g.rate}% of {g.total}</span>
                  {g.delta != null && g.delta !== 0 && (
                    <span className={`w-10 text-[9px] font-bold text-right ${g.delta < 0 ? 'text-emerald-600' : 'text-rose-600'}`}
                      title={`${Math.abs(g.delta)} ${g.delta < 0 ? 'fewer' : 'more'} gap answers than last week's snapshot`}
                      data-testid={`rfp-dash-gap-delta-${g.topic}`}>
                      {g.delta < 0 ? '▼' : '▲'}{Math.abs(g.delta)}
                    </span>
                  )}
                </button>
              ))}
            {data.gap_trend && data.gap_trend.length >= 2 && (
              <div className="pt-2 border-t border-slate-100" data-testid="rfp-dash-gap-trend">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Total gap answers by week — watch the heatmap cool down</p>
                <div className="flex items-end gap-1.5 h-14">
                  {data.gap_trend.map(w => {
                    const maxT = Math.max(1, ...data.gap_trend.map(x => x.total_gaps));
                    return (
                      <div key={w.week} className="flex-1 flex flex-col items-center gap-0.5" title={`${w.week}: ${w.total_gaps} gap answers`}>
                        <span className="text-[8px] text-slate-400">{w.total_gaps}</span>
                        <div className="w-full rounded-t bg-rose-300" style={{ height: `${(w.total_gaps / maxT) * 36}px` }} />
                        <span className="text-[7px] text-slate-400">{w.week.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="rfp-dash-reuse">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-[#0F2040]">
            <History className="w-4 h-4 text-[#4ABFCF]" />Reuse impact</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-xs">
            <p className="text-[10px] text-slate-400">Do RFPs reusing past accepted answers close better than freshly drafted ones?</p>
            <div className="flex items-center gap-2" data-testid="rfp-dash-reuse-with">
              <span className="w-32 text-slate-500">With reused answers</span>
              <Bar pct={r.win_rate_with ?? 0} cls="bg-emerald-500" />
              <span className="w-24 text-[10px] text-slate-400 text-right">{r.win_rate_with != null ? `${r.win_rate_with}% win` : 'no closed deals'}</span>
            </div>
            <div className="flex items-center gap-2" data-testid="rfp-dash-reuse-without">
              <span className="w-32 text-slate-500">Freshly drafted only</span>
              <Bar pct={r.win_rate_without ?? 0} cls="bg-slate-400" />
              <span className="w-24 text-[10px] text-slate-400 text-right">{r.win_rate_without != null ? `${r.win_rate_without}% win` : 'no closed deals'}</span>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-slate-100">
              <span className="text-slate-500">Avg client score — reused vs drafted</span>
              <span className="font-semibold text-[#0F2040]" data-testid="rfp-dash-reuse-scores">
                {r.avg_score_reused ?? '—'} vs {r.avg_score_drafted ?? '—'}
              </span>
            </div>
            <p className="text-[10px] text-slate-400">{r.reused_answers} answer{r.reused_answers === 1 ? '' : 's'} reused across all RFPs · {r.projects_with_reuse} closed deal{r.projects_with_reuse === 1 ? '' : 's'} contained reuse</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
